import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_REVIEW_COLLECTIONS,
  ACCESS_REVIEW_STATUS_LABELS,
  getCollection,
  type AccessReview,
  type AccessReviewCollection,
  type OrgMember,
  type OrgRecord,
  type Paginated,
  type RecordAssignment,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  Tabs,
  Textarea,
  formatDate,
  useToast,
} from '../../components/ui.js';
import {
  NextReview,
  REVIEW_STATUS_TONE,
  accessLevelLabel,
  addDaysIso,
  apiErrorMessage,
  defaultReviewName,
  localDateIso,
  reviewsKey,
  scopeLabel,
} from './AccessReviewShared.js';
import './records.css';

const PAGE_SIZE = 25;
/** Rows shown in the "Grants due for review" panel before it says "…and N more". */
const DUE_PANEL_ROWS = 30;

type Scope = 'all' | 'record' | 'privileged';

/** Access reviews list (/assets/software/access-reviews). Admins and reviewers only. */
export function AccessReviewsPage() {
  const { org } = useOrg();
  const { me } = useAuth();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const allowed = org.role === 'admin' || org.role === 'reviewer' || isSystemAdmin;

  const [offset, setOffset] = useState(0);
  const [dueView, setDueView] = useState<'overdue' | 'soon'>('overdue');
  const [startOpen, setStartOpen] = useState(false);

  const reviews = useQuery({
    queryKey: [...reviewsKey(org.orgId), 'list', offset],
    queryFn: () =>
      apiFetch<Paginated<AccessReview>>(`/orgs/${org.orgId}/access-reviews`, {
        query: { limit: PAGE_SIZE, offset },
      }),
    enabled: allowed,
  });
  // Same key as the dashboard card, so both read one cached response.
  const overdue = useQuery({
    queryKey: ['record-assignments', org.orgId, 'overdue'],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`, {
        query: { overdue: true },
      }),
    enabled: allowed,
  });
  // Every live grant, for the client-side "due in the next 30 days" view.
  const live = useQuery({
    queryKey: ['record-assignments', org.orgId, 'live'],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`),
    enabled: allowed,
  });

  const dueSoon = useMemo(() => {
    const today = localDateIso();
    const horizon = addDaysIso(today, 30);
    return (live.data?.items ?? []).filter(
      (a) => !a.overdue && a.nextReviewOn !== null && a.nextReviewOn >= today && a.nextReviewOn <= horizon,
    );
  }, [live.data]);

  if (!allowed) {
    return (
      <EmptyState
        title="Admins and reviewers only"
        message="Access reviews list colleagues' access levels and decisions, so only organisation admins and reviewers can open them. Your own grants are under My Software Access."
      />
    );
  }
  if (reviews.isLoading && offset === 0) return <LoadingPane label="Loading access reviews" />;

  const overdueItems = overdue.data?.items ?? [];
  const items = reviews.data?.items ?? [];
  const total = reviews.data?.total ?? 0;
  const shownDue = dueView === 'overdue' ? overdueItems : dueSoon;
  const startButton = <Button onClick={() => setStartOpen(true)}>Start review</Button>;

  return (
    <div>
      <PageHeader
        title="Access Reviews"
        subtitle="Re-certify who has access to what: confirm, change or revoke each grant, then have a second person approve the review"
        actions={startButton}
      />

      <Card title="Grants due for review" className="due-panel">
        <p className="coverage-hint">
          {overdueItems.length} overdue · {dueSoon.length} due in the next 30 days. Each grant is
          re-certified on the frequency set on its assignment; a review confirms, changes or revokes
          it and moves the date on.
        </p>
        <Tabs
          tabs={[
            { key: 'overdue', label: `Overdue (${overdueItems.length})` },
            { key: 'soon', label: `Due in 30 days (${dueSoon.length})` },
          ]}
          active={dueView}
          onChange={setDueView}
        />
        {shownDue.length === 0 ? (
          <p className="panel-empty">
            {dueView === 'overdue'
              ? 'No grants are overdue for review.'
              : 'Nothing falls due in the next 30 days.'}
          </p>
        ) : (
          <>
            <Table
              head={
                <>
                  <th>Person</th>
                  <th>System</th>
                  <th>Access level</th>
                  <th>Granted</th>
                  <th>Next review</th>
                </>
              }
            >
              {shownDue.slice(0, DUE_PANEL_ROWS).map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.userName}
                    <span className="cell-muted">{a.userEmail}</span>
                  </td>
                  <td>
                    {a.recordName}
                    <span className="cell-muted">
                      {getCollection(a.collection)?.singular ?? a.collection}
                    </span>
                  </td>
                  <td>
                    {accessLevelLabel(a.accessLevel)}
                    {a.accessRole && <span className="cell-muted">{a.accessRole}</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(a.grantedOn)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <NextReview assignment={a} />
                  </td>
                </tr>
              ))}
            </Table>
            {shownDue.length > DUE_PANEL_ROWS && (
              <p className="panel-empty">…and {shownDue.length - DUE_PANEL_ROWS} more grants.</p>
            )}
          </>
        )}
      </Card>

      {items.length === 0 ? (
        <EmptyState
          title="No access reviews yet"
          message="Start a review to snapshot the live grants in scope. You then confirm, change or revoke each one, complete the review and have a second person approve it."
          action={startButton}
        />
      ) : (
        <>
          <Table
            head={
              <>
                <th>Name</th>
                <th>Scope</th>
                <th>Reviewer</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Confirmed / Modified / Revoked</th>
                <th>Next review</th>
                <th>Approved by</th>
                <th />
              </>
            }
          >
            {items.map((r) => {
              const decided = r.counts.total - r.counts.pending;
              return (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>
                    <Link to={`/assets/software/access-reviews/${r.id}`}>{r.name}</Link>
                    <span className="cell-muted">Started {formatDate(r.createdAt)}</span>
                  </td>
                  <td>{scopeLabel(r)}</td>
                  <td>{r.reviewerName}</td>
                  <td>
                    <Badge tone={REVIEW_STATUS_TONE[r.status]}>
                      {ACCESS_REVIEW_STATUS_LABELS[r.status]}
                    </Badge>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {decided} / {r.counts.total}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.counts.confirmed} / {r.counts.modified} / {r.counts.revoked}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.nextReviewOn)}</td>
                  <td>{r.approverName ?? '—'}</td>
                  <td>
                    <div className="record-row-actions">
                      <Link to={`/assets/software/access-reviews/${r.id}`}>
                        <Button variant="secondary" size="sm">
                          Open
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>

          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <StartReviewDialog open={startOpen} onClose={() => setStartOpen(false)} />
    </div>
  );
}

/** "Start review": name, scope (whole collection / one system / privileged only), reviewer, notes. */
function StartReviewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { org } = useOrg();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState(() => defaultReviewName());
  const [collection, setCollection] = useState<AccessReviewCollection>('software');
  const [scope, setScope] = useState<Scope>('all');
  const [recordId, setRecordId] = useState('');
  const [reviewerId, setReviewerId] = useState('');
  const [notes, setNotes] = useState('');

  // Fresh defaults every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(defaultReviewName());
    setCollection('software');
    setScope('all');
    setRecordId('');
    setReviewerId(me?.user.id ?? '');
    setNotes('');
  }, [open, me?.user.id]);

  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: open,
  });
  // Same key/query as the record wizard's reference options.
  const records = useQuery({
    queryKey: ['records', org.orgId, collection, 'active'],
    queryFn: () =>
      apiFetch<Paginated<OrgRecord>>(`/orgs/${org.orgId}/records`, {
        query: { collection, status: 'active', limit: 200 },
      }),
    enabled: open && scope === 'record',
  });

  const create = useMutation({
    mutationFn: () =>
      apiFetch<AccessReview>(`/orgs/${org.orgId}/access-reviews`, {
        method: 'POST',
        body: {
          name: name.trim(),
          collection,
          recordId: scope === 'record' ? recordId : null,
          privilegedOnly: scope === 'privileged',
          reviewerId: reviewerId || undefined,
          notes: notes.trim(),
        },
      }),
    onSuccess: (review) => {
      void queryClient.invalidateQueries({ queryKey: reviewsKey(org.orgId) });
      toast(`${review.name} started with ${review.counts.total} grants to decide.`, 'success');
      onClose();
      navigate(`/assets/software/access-reviews/${review.id}`);
    },
    onError: (err) => toast(apiErrorMessage(err, 'Could not start the review'), 'error'),
  });

  const reviewers = (members.data ?? []).filter(
    (m) => (m.role === 'admin' || m.role === 'reviewer') && m.status === 'active',
  );
  const collectionName = (getCollection(collection)?.name ?? collection).toLowerCase();
  // A system admin who is not a member still needs a selectable default.
  const reviewerMissing =
    reviewerId !== '' && members.data !== undefined && !reviewers.some((m) => m.user.id === reviewerId);
  const canSubmit = name.trim().length > 0 && (scope !== 'record' || recordId !== '');

  return (
    <Dialog open={open} onClose={onClose} title="Start an access review">
      <div className="record-dialog">
        <Field label="Name" required>
          {(props) => (
            <Input
              {...props}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Collection">
          {(props) => (
            <Select
              {...props}
              value={collection}
              onChange={(e) => {
                setCollection(e.target.value as AccessReviewCollection);
                setRecordId('');
              }}
            >
              {ACCESS_REVIEW_COLLECTIONS.map((key) => (
                <option key={key} value={key}>
                  {getCollection(key)?.name ?? key}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset className="scope-fieldset">
          <legend className="field-label">Scope</legend>
          <div className="radio-row">
            <label>
              <input
                type="radio"
                name="review-scope"
                value="all"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
              />
              All {collectionName}
            </label>
            <label>
              <input
                type="radio"
                name="review-scope"
                value="record"
                checked={scope === 'record'}
                onChange={() => setScope('record')}
              />
              One system
            </label>
            <label>
              <input
                type="radio"
                name="review-scope"
                value="privileged"
                checked={scope === 'privileged'}
                onChange={() => setScope('privileged')}
              />
              Privileged grants only
            </label>
          </div>
          <span className="field-hint">
            Every live grant in scope is snapshotted when the review starts; grants added later are
            picked up by the next review.
          </span>
        </fieldset>
        {scope === 'record' && (
          <Field label="System" required>
            {(props) => (
              <Select {...props} value={recordId} onChange={(e) => setRecordId(e.target.value)}>
                <option value="">Select a system…</option>
                {(records.data?.items ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label="Reviewer" hint="Only the reviewer or an admin can decide and complete the review">
          {(props) => (
            <Select {...props} value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}>
              {reviewerId === '' && <option value="">Me</option>}
              {reviewerMissing && <option value={reviewerId}>{me?.user.name ?? 'Me'}</option>}
              {reviewers.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {m.user.name} ({m.role})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Notes" hint="Optional — context for the report">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={notes}
              maxLength={5000}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} loading={create.isPending} onClick={() => create.mutate()}>
            Start review
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
